import { Device, Call } from "@twilio/voice-sdk";
import CallData from "./CallData";
import CallStatus from "./CallStatus";
import TwilioCall from "./TwilioCall";
import {
  GoogleCloudTranscriptionService,
  GoogleCloudTranscriptionConfig,
  TranscriptionResult as GoogleTranscriptionResult,
} from "./GoogleCloudTranscriptionService";

export default class TwilioClient {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  public data: CallData | null = null;
  public callSid: string | null = null;

  // Google Cloud transcription support
  private googleCloudTranscription: GoogleCloudTranscriptionService | null =
    null;

  // Add server-side transcription support
  private serverTranscriptionEnabled: boolean = false;
  private serverTranscriptionCallback:
    | ((text: string, isFinal: boolean, speaker: "local" | "remote") => void)
    | null = null;

  private registrationAttempts: number = 0;
  private maxRegistrationAttempts: number = 5;
  private registrationTimeout: NodeJS.Timeout | null = null;

  constructor(public accessToken: string) {
    // Initialize data property if not already set
    if (!this.data) {
      console.warn(
        "CallData not initialized. Call functionality may not work properly."
      );
    }
  }

  get isMuted(): boolean {
    return this.activeCall ? this.activeCall.isMuted() : false;
  }

  private log(
    level: "info" | "warn" | "error",
    message: string,
    ...args: any[]
  ) {
    const timestamp = new Date().toISOString();
    console[level](`[${timestamp}] ${message}`, ...args);
  }

  // Centralized call state management
  private updateCallState(
    status: CallStatus,
    callSid: string | null,
    shouldCleanup: boolean = false
  ) {
    this.log("info", `Call state updated: ${status}, callSid: ${callSid}`);

    if (this.data) {
      this.data.setCallStatus(status);

      // Only clear callSid for final states or when explicitly requested
      if (shouldCleanup || this.isFinalCallState(status)) {
        this.data.setCallSid(null);
        this.callSid = null;
        this.activeCall = null;
      } else if (callSid) {
        this.data.setCallSid(callSid);
        this.callSid = callSid;
      }
    }
  }

  private isFinalCallState(status: CallStatus): boolean {
    return [
      CallStatus.Completed,
      CallStatus.Failed,
      CallStatus.Busy,
      CallStatus.Rejected,
      CallStatus.Cancelled,
      CallStatus.Unanswered,
    ].includes(status);
  }

  private logCallStateChange(
    fromStatus: CallStatus | null,
    toStatus: CallStatus,
    callSid: string | null
  ) {
    this.log(
      "info",
      `Call state transition: ${fromStatus} -> ${toStatus}, callSid: ${callSid}`
    );

    if (this.data) {
      this.log(
        "info",
        `Current data state: deviceReady=${this.data.deviceReady}, callSid=${this.data.callSid}, callStatus=${this.data.callStatus}`
      );
    }
  }

  public shouldKeepCallActive(): boolean {
    if (!this.data || !this.activeCall) return false;

    return [
      CallStatus.Initiated,
      CallStatus.Ringing,
      CallStatus.Connecting,
      CallStatus.InProgress,
    ].includes(this.data.callStatus);
  }

  public getCurrentCallInfo() {
    return {
      callSid: this.callSid,
      callStatus: this.data?.callStatus,
      deviceReady: this.data?.deviceReady,
      isActive: this.activeCall !== null,
    };
  }

  /**
   * Set the CallData instance for this client
   */
  public setCallData(callData: CallData): void {
    this.data = callData;
    this.log("info", "CallData initialized");
  }

  // Google Cloud transcription methods

  /**
   * Check if transcription is supported in the current browser
   */
  public static isTranscriptionSupported(): boolean {
    return GoogleCloudTranscriptionService.isSupported();
  }

  /**
   * Enable Twilio's server-side transcription (captures both sides of the call)
   * This is the recommended approach for capturing both parties' speech
   */
  public async enableServerTranscription(
    callback: (
      text: string,
      isFinal: boolean,
      speaker: "local" | "remote"
    ) => void,
    options: {
      language?: string;
      enablePartialResults?: boolean;
      enableInterimResults?: boolean;
    } = {}
  ): Promise<boolean> {
    try {
      if (!this.activeCall || !this.callSid) {
        this.log("warn", "No active call for server transcription");
        return false;
      }

      this.log("info", "Enabling Twilio server-side transcription...");

      // Store the callback for server transcription events
      this.serverTranscriptionCallback = callback;
      this.serverTranscriptionEnabled = true;

      // Note: Server-side transcription is typically enabled when the call is initiated
      // via TwiML or when using the Twilio Voice SDK with specific parameters
      // The actual transcription results will come through webhook events or the call object

      this.log(
        "info",
        "Server transcription enabled - both sides of call will be transcribed"
      );
      return true;
    } catch (error) {
      this.log("error", "Failed to enable server transcription:", error);
      return false;
    }
  }

  /**
   * Disable server-side transcription
   */
  public disableServerTranscription(): void {
    this.serverTranscriptionEnabled = false;
    this.serverTranscriptionCallback = null;
    this.log("info", "Server transcription disabled");
  }

  /**
   * Check if server-side transcription is enabled
   */
  public isServerTranscriptionEnabled(): boolean {
    return this.serverTranscriptionEnabled;
  }

  /**
   * Enable Google Cloud transcription (captures both sides of the call)
   * Cost: $0.036 per minute (still cheaper than Twilio's $0.024-0.027/min)
   */
  public async enableGoogleCloudTranscription(
    config: GoogleCloudTranscriptionConfig,
    callback: (result: GoogleTranscriptionResult) => void
  ): Promise<boolean> {
    try {
      if (!this.activeCall) {
        this.log("error", "❌ No active call for Google Cloud transcription");
        return false;
      }

      if (!config.apiKey) {
        this.log("error", "❌ Google Cloud API key is required");
        return false;
      }

      if (!config.projectId) {
        this.log("error", "❌ Google Cloud project ID is required");
        return false;
      }

      // Initialize Google Cloud transcription service
      this.googleCloudTranscription = new GoogleCloudTranscriptionService(
        config
      );

      // Get audio streams from the call
      const localStream = await this.getLocalAudioStream();
      const remoteStream = await this.getRemoteAudioStream();

      if (!localStream && !remoteStream) {
        this.log("error", "❌ No audio streams available for transcription");
        return false;
      }

      // Start transcription
      const success = await this.googleCloudTranscription.startTranscription(
        callback,
        localStream,
        remoteStream
      );

      if (success) {
        this.log("info", "✅ Google Cloud transcription enabled successfully");
      } else {
        this.log("error", "❌ Failed to enable Google Cloud transcription");
      }

      return success;
    } catch (error) {
      this.log("error", "❌ Error in Google Cloud transcription setup:", error);
      return false;
    }
  }

  /**
   * Disable Google Cloud transcription
   */
  public disableGoogleCloudTranscription(): void {
    if (this.googleCloudTranscription) {
      this.googleCloudTranscription.stopTranscription();
      this.googleCloudTranscription.dispose();
      this.googleCloudTranscription = null;
      this.log("info", "Google Cloud transcription disabled");
    }
  }

  /**
   * Check if Google Cloud transcription is active
   */
  public isGoogleCloudTranscriptionActive(): boolean {
    return this.googleCloudTranscription?.isTranscriptionActive() || false;
  }

  /**
   * Check if Google Cloud transcription is available and configured
   */
  public isGoogleCloudTranscriptionAvailable(): boolean {
    return !!this.googleCloudTranscription;
  }

  /**
   * Check if Google Cloud is configured with API key and project ID
   */
  private isGoogleCloudConfigured(): boolean {
    // Check if we have a configuration file or environment variables
    return this.hasGoogleCloudCredentials();
  }

  /**
   * Get Google Cloud configuration
   */
  private getGoogleCloudConfig(): GoogleCloudTranscriptionConfig | null {
    return this.getGoogleCloudCredentials();
  }

  /**
   * Check if Google Cloud credentials are available
   */
  private hasGoogleCloudCredentials(): boolean {
    // You can modify this to check your configuration source
    // For now, we'll check if there's a config file or environment variables
    return this.getGoogleCloudCredentials() !== null;
  }

  /**
   * Get Google Cloud credentials from configuration
   */
  private getGoogleCloudCredentials(): GoogleCloudTranscriptionConfig | null {
    // You can modify this method to load from your configuration source
    // For example: config file, environment variables, database, etc.

    // Option 1: Check if there's a global config object
    if (typeof window !== "undefined" && (window as any).GOOGLE_CLOUD_CONFIG) {
      const config = (window as any).GOOGLE_CLOUD_CONFIG;
      this.log("info", "☁️ Checking GOOGLE_CLOUD_CONFIG in window object");
      this.log("info", `☁️ Config found: ${!!config}`);
      this.log("info", `☁️ API Key present: ${!!config.apiKey}`);
      this.log("info", `☁️ Project ID present: ${!!config.projectId}`);

      if (config.apiKey && config.projectId) {
        this.log("info", "☁️ Found valid Google Cloud config in window object");
        return config;
      } else {
        this.log(
          "warn",
          "☁️ GOOGLE_CLOUD_CONFIG found but missing required fields"
        );
      }
    }

    // Option 2: Check environment variables (if available)
    if (typeof process !== "undefined" && process.env) {
      const apiKey = process.env.GOOGLE_CLOUD_API_KEY;
      const projectId = process.env.GOOGLE_CLOUD_PROJECT_ID;
      if (apiKey && projectId) {
        this.log(
          "info",
          "☁️ Found Google Cloud config in environment variables"
        );
        return {
          apiKey,
          projectId,
          languageCode: "en-US",
          enableInterimResults: true,
          enableSpeakerDiarization: true,
          maxSpeakers: 2,
          model: "phone_call",
          sampleRate: 16000,
          audioChannels: 1,
        };
      }
    }

    // Option 3: Check if there's a config file loaded
    if (typeof window !== "undefined" && (window as any).googleCloudConfig) {
      const config = (window as any).googleCloudConfig;
      this.log("info", "☁️ Checking googleCloudConfig in window object");
      this.log("info", `☁️ Config found: ${!!config}`);
      this.log("info", `☁️ API Key present: ${!!config.apiKey}`);
      this.log("info", `☁️ Project ID present: ${!!config.projectId}`);

      if (config.apiKey && config.projectId) {
        this.log(
          "info",
          "☁️ Found valid Google Cloud config in googleCloudConfig"
        );
        return config;
      } else {
        this.log(
          "warn",
          "☁️ googleCloudConfig found but missing required fields"
        );
      }
    }

    // Option 4: Try to import the config file directly
    try {
      this.log("info", "☁️ Attempting to import Google Cloud config file...");
      // This is a fallback - the file should be loaded by the browser
      this.log(
        "info",
        "☁️ If you see this message, the config file may not be imported"
      );
    } catch (error) {
      this.log("warn", "☁️ Failed to import Google Cloud config file:", error);
    }

    this.log("warn", "☁️ No valid Google Cloud configuration found");
    this.log(
      "warn",
      "☁️ Please ensure google-cloud-credentials.ts is imported in your app"
    );
    return null;
  }

  /**
   * Get transcription status for debugging
   */
  public getTranscriptionStatus(): {
    google: boolean;
    hasActiveCall: boolean;
    callSid: string | null;
  } {
    return {
      google: this.isGoogleCloudTranscriptionActive(),
      hasActiveCall: !!this.activeCall,
      callSid: this.callSid,
    };
  }

  /**
   * Get local audio stream from microphone
   */
  private async getLocalAudioStream(): Promise<MediaStream | null> {
    try {
      this.log("info", "🎤 Requesting microphone permission...");
      this.log("info", "🎤 Checking if getUserMedia is supported...");

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.log("error", "❌ getUserMedia not supported in this browser");
        return null;
      }

      this.log("info", "🎤 getUserMedia supported, requesting audio...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      this.log("info", "🎤 Microphone permission granted successfully!");
      this.log("info", `🎤 Stream tracks: ${stream.getTracks().length}`);
      this.log("info", `🎤 Stream active: ${stream.active}`);
      this.log("info", `🎤 Stream ID: ${stream.id}`);

      return stream;
    } catch (error) {
      this.log("error", "❌ Failed to get local audio stream:");
      this.log(
        "error",
        `Error type: ${
          error instanceof Error ? error.constructor.name : "Unknown"
        }`
      );
      this.log(
        "error",
        `Error message: ${
          error instanceof Error ? error.message : String(error)
        }`
      );

      if (error instanceof Error && error.name === "NotAllowedError") {
        this.log("error", "❌ Microphone permission denied by user");
      } else if (error instanceof Error && error.name === "NotFoundError") {
        this.log("error", "❌ No microphone found on device");
      } else if (error instanceof Error && error.name === "NotReadableError") {
        this.log(
          "error",
          "❌ Microphone is already in use by another application"
        );
      }

      return null;
    }
  }

  /**
   * Get remote audio stream from the call
   */
  private async getRemoteAudioStream(): Promise<MediaStream | null> {
    try {
      this.log("info", "📞 Attempting to get remote audio stream...");

      if (!this.activeCall) {
        this.log("warn", "⚠️ No active call available for remote audio");
        return null;
      }

      this.log("info", `📞 Active call status: ${this.activeCall.status()}`);
      this.log(
        "info",
        `📞 Active call direction: ${this.activeCall.direction}`
      );
      this.log(
        "info",
        `📞 Active call parameters:`,
        this.activeCall.parameters
      );

      // Try to get the remote stream from the Twilio call
      this.log(
        "info",
        "📞 Trying to get remote stream from Twilio call object..."
      );
      const remoteStream = (this.activeCall as any).getRemoteStream?.();

      if (remoteStream) {
        this.log("info", "✅ Remote stream obtained from Twilio call object!");
        this.log(
          "info",
          `📞 Remote stream tracks: ${remoteStream.getTracks().length}`
        );
        this.log("info", `📞 Remote stream active: ${remoteStream.active}`);
        this.log("info", `📞 Remote stream ID: ${remoteStream.id}`);
        return remoteStream;
      } else {
        this.log(
          "warn",
          "⚠️ getRemoteStream() method not available on Twilio call"
        );
      }

      // Alternative: Create a MediaStream from the call's audio element
      this.log("info", "📞 Trying to get remote stream from audio elements...");
      const audioElements = document.querySelectorAll("audio");
      this.log(
        "info",
        `📞 Found ${audioElements.length} audio elements on page`
      );

      for (let i = 0; i < audioElements.length; i++) {
        const audio = audioElements[i];
        this.log(
          "info",
          `📞 Audio element ${i}: src="${
            audio.src
          }", srcObject=${!!audio.srcObject}`
        );

        if (audio.srcObject && audio.srcObject instanceof MediaStream) {
          const stream = audio.srcObject;
          this.log("info", `✅ Remote stream found in audio element ${i}!`);
          this.log(
            "info",
            `📞 Remote stream tracks: ${stream.getTracks().length}`
          );
          this.log("info", `📞 Remote stream active: ${stream.active}`);
          this.log("info", `📞 Remote stream ID: ${stream.id}`);
          return stream;
        }
      }

      this.log(
        "warn",
        "⚠️ Could not access remote audio stream - transcription will be local only"
      );
      this.log("info", "📞 This is normal for some Twilio configurations");
      this.log(
        "info",
        "📞 Local transcription will still work (your voice only)"
      );
      return null;
    } catch (error) {
      this.log("error", "❌ Failed to get remote audio stream:");
      this.log(
        "error",
        `Error type: ${
          error instanceof Error ? error.constructor.name : "Unknown"
        }`
      );
      this.log(
        "error",
        `Error message: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
      return null;
    }
  }

  /**
   * Enable Google Cloud transcription
   */
  public async enableTranscription(
    callback: (text: string, isFinal: boolean) => void,
    googleConfig: GoogleCloudTranscriptionConfig
  ): Promise<boolean> {
    try {
      if (!this.activeCall) {
        this.log("error", "❌ No active call for transcription");
        return false;
      }

      if (!googleConfig) {
        this.log("error", "❌ Google Cloud configuration required");
        return false;
      }

      const googleCallback = (result: GoogleTranscriptionResult) => {
        // Call the original callback with just the text
        callback(result.text, result.isFinal);

        // Log with speaker identification
        const speakerLabel =
          result.audioSource === "local"
            ? "👤 Your voice"
            : result.audioSource === "remote"
            ? "📞 Other party"
            : "🔀 Mixed audio";
        this.log("info", `🎯 ${speakerLabel}: ${result.text}`);
      };

      return await this.enableGoogleCloudTranscription(
        googleConfig,
        googleCallback
      );
    } catch (error) {
      this.log("error", "💥 CRITICAL ERROR in transcription setup:", error);
      return false;
    }
  }

  /**
   * Disable transcription
   */
  public disableTranscription(): void {
    // Stop Google Cloud transcription
    if (this.googleCloudTranscription) {
      this.googleCloudTranscription.stopTranscription();
      this.googleCloudTranscription.dispose();
      this.googleCloudTranscription = null;
    }
  }

  /**
   * Check if transcription is currently active
   */
  public isTranscriptionEnabled(): boolean {
    return this.isGoogleCloudTranscriptionActive();
  }

  /**
   * Test transcription functionality
   */
  public async testTranscription(): Promise<boolean> {
    try {
      this.log("info", "Testing transcription functionality...");

      // Check browser support
      if (!TwilioClient.isTranscriptionSupported()) {
        this.log("error", "Transcription not supported in this browser");
        return false;
      }

      // Check microphone permission
      const hasPermission = await this.checkMicPermission();
      if (!hasPermission) {
        this.log("error", "Microphone permission required for transcription");
        return false;
      }

      this.log("info", "Transcription test successful");
      return true;
    } catch (error) {
      this.log("error", "Transcription test failed:", error);
      return false;
    }
  }

  /**
   * Debug transcription status - useful for troubleshooting
   */
  public debugTranscriptionStatus(): void {
    this.log("info", "=== Transcription Debug Info ===");
    this.log("info", `Active Call: ${!!this.activeCall}`);
    this.log("info", `CallSid: ${this.callSid}`);
    this.log(
      "info",
      `Transcription Active: ${this.isGoogleCloudTranscriptionActive()}`
    );
    this.log(
      "info",
      `Browser Support: ${TwilioClient.isTranscriptionSupported()}`
    );

    if (this.activeCall) {
      this.log("info", `Call Muted: ${this.activeCall.isMuted()}`);
      this.log("info", `Call Status: ${this.activeCall.status()}`);
    }

    this.log("info", "=== End Debug Info ===");
  }

  /**
   * Auto-enable transcription when calls become active
   */
  private async autoEnableTranscription(): Promise<void> {
    try {
      // ✅ Better logging and validation
      this.log(
        "info",
        `Auto-transcription check - activeCall: ${!!this
          .activeCall}, callSid: ${this.callSid}`
      );

      // Check if we have an active call
      if (!this.activeCall) {
        this.log("warn", "No active call for auto-transcription");
        return;
      }

      if (!this.callSid) {
        this.log("warn", "No callSid for auto-transcription");
        return;
      }

      // Check if transcription is already enabled
      if (this.isTranscriptionEnabled()) {
        this.log("info", "Transcription already enabled, skipping auto-enable");
        return;
      }

      // ✅ Check if call is muted
      if (this.activeCall.isMuted()) {
        this.log("warn", "Call is muted, cannot start transcription");
        return;
      }

      this.log("info", "Auto-enabling transcription...");

      // Use default transcription callback
      const defaultCallback = (text: string, isFinal: boolean) => {
        if (isFinal) {
          this.log("info", `Final transcript: ${text}`);
          // You can add custom logic here, like saving to database
        }
      };

      // Check if Google Cloud is configured
      this.log("info", "☁️ Checking if Google Cloud is configured...");
      const isConfigured = this.isGoogleCloudConfigured();
      this.log("info", `☁️ Google Cloud configured: ${isConfigured}`);

      if (isConfigured) {
        this.log(
          "info",
          "☁️ Attempting to auto-enable Google Cloud transcription..."
        );

        try {
          const googleConfig = this.getGoogleCloudConfig();
          this.log("info", `☁️ Google config obtained: ${!!googleConfig}`);

          if (googleConfig) {
            this.log(
              "info",
              "☁️ Starting Google Cloud transcription with config..."
            );
            const success = await this.enableTranscription(
              defaultCallback,
              googleConfig
            );

            if (success) {
              this.log(
                "info",
                "☁️ Google Cloud auto-transcription enabled successfully"
              );
              return;
            } else {
              this.log("warn", "☁️ Google Cloud auto-transcription failed");
            }
          } else {
            this.log("warn", "☁️ Failed to get Google Cloud config");
          }
        } catch (error) {
          this.log("warn", "☁️ Google Cloud auto-transcription error:", error);
        }
      } else {
        this.log("info", "☁️ Google Cloud not configured");
      }
    } catch (error) {
      this.log("error", "Auto-transcription failed:", error);
    }
  }

  /**
   * Toggle transcription on/off during a call
   */
  public async toggleTranscription(): Promise<boolean> {
    try {
      if (this.isTranscriptionEnabled()) {
        this.disableTranscription();
        this.log("info", "Transcription disabled");
        return true;
      } else {
        // Start with default callback
        const defaultCallback = (text: string, isFinal: boolean) => {
          if (isFinal) {
            this.log("info", `Final transcript: ${text}`);
          }
        };

        // Check if Google Cloud is configured
        const googleConfig = this.getGoogleCloudConfig();
        if (!googleConfig) {
          this.log("error", "Google Cloud not configured for transcription");
          return false;
        }

        return await this.enableTranscription(defaultCallback, googleConfig);
      }
    } catch (error) {
      this.log("error", "Failed to toggle transcription:", error);
      return false;
    }
  }

  async checkMicPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      this.log("error", "Microphone permission denied:", error);
      return false;
    }
  }

  private isValidAccessToken(token: string): boolean {
    return typeof token === "string" && token.trim().length > 0;
  }

  public setAccessToken(token: string) {
    this.accessToken = token;
  }

  private async refreshToken() {
    try {
      // Call your backend to get a new token
      const response = await fetch("/get-new-token");
      const { token } = await response.json();
      this.accessToken = token;
      await this.device?.updateToken(token);
      this.log("info", "Token refreshed successfully");
    } catch (error) {
      this.log("error", "Failed to refresh token:", error);
    }
  }

  async checkRequirements(): Promise<boolean> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.log("error", "WebRTC is not supported in this browser");
      return false;
    }

    if (!navigator.onLine) {
      this.log("error", "No internet connection");
      return false;
    }

    return true;
  }

  async initialize({
    onSuccess,
    onFailure,
    onIncomingCall,
    onTokenWillExpire,
  }: {
    onSuccess: (message: string) => void;
    onFailure: (message: string, error: string) => void;
    onIncomingCall: (call: TwilioCall) => void;
    onTokenWillExpire(token: string): void;
  }): Promise<boolean> {
    try {
      if (!this.isValidAccessToken(this.accessToken)) {
        throw new Error("Invalid access token format");
      }
      if (!(await this.checkRequirements())) {
        onFailure("Initialization failed", "Requirements not met");
        return false;
      }
      this.device = new Device(this.accessToken, {
        logLevel: 3, // Warning level
        edge: [
          "ashburn",
          "dublin",
          "singapore",
          "sydney",
          "tokyo",
          "sao-paulo",
          "frankfurt",
        ],
      });

      this.device.on("ready", () => {
        this.log("info", "Device is ready for incoming calls.");
      });

      this.device.on("tokenWillExpire", () => {
        console.warn("Token will expire soon. Refreshing..." + new Date());
        onTokenWillExpire(this.accessToken);
      });

      // Listen for token expiration
      this.device.on("tokenExpired", () => {
        console.error("Token expired. Fetching a new one..." + new Date());
      });

      this.device.on("incoming", (con) => {
        this.log("info", "Incoming call received");
        // Accept the incoming connection
        const parameters = con.parameters;
        const customParams = this.parseCustomParams(parameters.Params);
        const originalCallSid = customParams.OriginalCallSid || "";
        console.log(
          "from : ",
          con.parameters.From,
          " to : ",
          con.parameters.To,
          " callSid : ",
          con.parameters.CallSid,
          "OriginalCallSid : ",
          originalCallSid
        );
        onIncomingCall(
          new TwilioCall(
            con,
            originalCallSid,
            con.parameters.From,
            con.parameters.To
          )
        );
        this.attachCallListeners(con);
      });
      this.device.on("registered", () => {
        this.log("info", "Twilio device registered");
        this.data.setDeviceReady(true);
        onSuccess("Device registered successfully");

        this.registrationAttempts = 0;
        if (this.registrationTimeout) {
          clearTimeout(this.registrationTimeout);
        }
      });

      this.device.on("error", (error) => {
        this.log("error", "Twilio device error:", error);
        this.log("info", "Error code:", error.code);
        this.log("info", "Error message:", error.message);
      });

      this.device.on("disconnect", (connection) => {
        this.log("info", "Call disconnected:", connection);
        if (connection.parameters && connection.parameters.CallSid) {
          this.log("info", "CallSid:", connection.parameters.CallSid);
        }
      });

      this.device.on("hangup", (payload) => {
        if (payload.error && payload.error.code === 31404) {
          this.log(
            "error",
            "Hangup due to Not Found error:",
            payload.error.message
          );
        } else {
          this.log("info", "Call hangup received:", payload);
        }
      });

      await this.device.register();

      return true;
    } catch (error) {
      this.log("error", "Failed to initialize Twilio device:", error);
      onFailure("Initialization failed", (error as Error).message);
      return false;
    }
  }

  public async updateDeviceToken(token: string): Promise<boolean> {
    {
      try {
        this.accessToken = token;
        await this.device?.updateToken(token);
        return true;
      } catch (error) {
        this.log("error", "Failed to update device token:", error);
        return false;
      }
    }
  }
  public async sendDigit(digit: string): Promise<boolean> {
    if (this.activeCall) {
      this.activeCall.sendDigits(digit); // supports 0-9, *, #, and 'w' for 0.5s pause
    }
    return true;
  }

  private attachCallListeners(con: any): void {
    con.on("cancel", () => this.handleCallCancelled());
    con.on("reject", () => this.handleCallRejected());
    con.on("disconnect", () => this.handleCallDisconnected());
    con.on("error", (error: Error) => this.handleConnectionError(error));
  }

  private handleCallCancelled(): void {
    this.log(
      "info",
      "Caller hung up before the call was answered. or Other device cancelled the call."
    );
    this.cleanupCall(CallStatus.Cancelled);
  }

  // Handle call reject event (Callee rejects the call)
  private handleCallRejected(): void {
    this.log("info", "Callee rejected the call.");
    this.cleanupCall(CallStatus.Rejected);
  }

  // Handle disconnect event (Call was answered and then disconnected)
  private handleCallDisconnected(): void {
    this.log("info", "Call disconnected.");
    this.cleanupCall(CallStatus.Completed);
  }

  // Handle connection errors
  private handleConnectionError(error: Error): void {
    this.log("error", `Connection error: ${error.message}`);
  }

  /**
   * Clean up transcription when call ends
   */
  private cleanupTranscription(): void {
    try {
      if (this.googleCloudTranscription) {
        this.googleCloudTranscription.stopTranscription();
        this.googleCloudTranscription.dispose();
        this.googleCloudTranscription = null;
        this.log("info", "Google Cloud transcription cleaned up");
      }
    } catch (error) {
      this.log("error", "Failed to cleanup transcription:", error);
    }
  }

  // Clean up call state and reset relevant variables
  private cleanupCall(status: CallStatus): void {
    this.data.setCallStatus(status);
    this.data.setCallSid(null);
    this.callSid = null;
    this.activeCall = null;

    // Clean up transcription
    this.cleanupTranscription();
  }
  private parseCustomParams(paramsString: string): Record<string, string> {
    const params: Record<string, string> = {};
    if (paramsString) {
      paramsString.split("&").forEach((param) => {
        const [key, value] = param.split("=");
        params[key] = value;
      });
    }
    return params;
  }

  private attemptReregistration() {
    if (this.registrationAttempts >= this.maxRegistrationAttempts) {
      this.log(
        "error",
        "Max registration attempts reached. Please check your connection and try again later."
      );
      return;
    }

    this.registrationAttempts++;
    this.log(
      "info",
      `Attempting to re-register device (Attempt ${this.registrationAttempts})`
    );

    this.registrationTimeout = setTimeout(async () => {
      try {
        console.log("Attempting to re-register device...");
        await this.device?.register();
      } catch (error) {
        this.log("error", "Re-registration attempt failed:", error);
        this.attemptReregistration();
      }
    }, 5000 * this.registrationAttempts); // Increasing backoff
  }

  async acceptIncomingCall(
    call: TwilioCall,
    {
      onStatus,
      onFailure,
    }: {
      onStatus: (message: string) => void;
      onFailure: (message: string, error: string) => void;
    }
  ): Promise<boolean> {
    try {
      this.activeCall = call.call;

      // ✅ Set callSid immediately from the call parameters
      if (this.activeCall.parameters.CallSid) {
        this.callSid = this.activeCall.parameters.CallSid;
        this.log("info", `CallSid set: ${this.callSid}`);
      }

      await this.activeCall.accept();
      this.setupCallEventListeners(onStatus, onFailure);
      onStatus("Call accepted");
      this.updateCallState(CallStatus.InProgress, this.callSid, false);
      return true;
    } catch (error) {
      onFailure("Failed to accept incoming call", (error as Error).message);
      return false;
    }
  }

  async rejectIncomingCall(call: TwilioCall): Promise<boolean> {
    try {
      await call.call.reject();
      return true;
    } catch (error) {
      this.log("error", "Failed to reject incoming call:", error);
      return false;
    }
  }

  private monitorCallQuality() {
    if (this.activeCall) {
      this.activeCall.on("sample", (sample) => {
        this.data.setCallStatus(CallStatus.InProgress);

        // Enhanced call quality monitoring for transcription
        this.analyzeCallQualitySample(sample);
      });
    }
  }

  private analyzeCallQualitySample(sample: any) {
    // Log key quality metrics that affect transcription
    if (sample.packetLoss !== undefined) {
      if (sample.packetLoss > 0.05) {
        this.log(
          "warn",
          `High packet loss detected: ${(sample.packetLoss * 100).toFixed(
            2
          )}% - may affect transcription quality`
        );
      } else if (sample.packetLoss > 0.02) {
        this.log(
          "info",
          `Moderate packet loss: ${(sample.packetLoss * 100).toFixed(2)}%`
        );
      }
    }

    if (sample.jitter !== undefined) {
      if (sample.jitter > 50) {
        this.log(
          "warn",
          `High jitter detected: ${sample.jitter.toFixed(
            2
          )}ms - may affect transcription accuracy`
        );
      } else if (sample.jitter > 20) {
        this.log("info", `Moderate jitter: ${sample.jitter.toFixed(2)}ms`);
      }
    }

    if (sample.rtt !== undefined) {
      if (sample.rtt > 300) {
        //this.log("warn", `High latency detected: ${sample.rtt.toFixed(2)}ms - may affect transcription responsiveness`);
      } else if (sample.rtt > 150) {
        // this.log("info", `Moderate latency: ${sample.rtt.toFixed(2)}ms`);
      }
    }

    // Emit call quality metrics for UI components
    if (typeof window !== "undefined" && window.dispatchEvent) {
      const callQualityEvent = new CustomEvent("call-quality-update", {
        detail: {
          sample,
          timestamp: new Date().toISOString(),
          quality: this.calculateQualityScore(sample),
        },
      });
      window.dispatchEvent(callQualityEvent);
    }
  }

  private calculateQualityScore(
    sample: any
  ): "excellent" | "good" | "fair" | "poor" {
    let score = 100;

    // Deduct points for poor metrics that affect transcription
    if (sample.packetLoss > 0.05) score -= 30;
    else if (sample.packetLoss > 0.02) score -= 15;

    if (sample.jitter > 50) score -= 25;
    else if (sample.jitter > 20) score -= 10;

    if (sample.rtt > 300) score -= 20;
    else if (sample.rtt > 150) score -= 10;

    if (score >= 90) return "excellent";
    if (score >= 75) return "good";
    if (score >= 60) return "fair";
    return "poor";
  }

  /**
   * Get current call quality information for transcription
   */
  public getCallQualityInfo(): {
    isActive: boolean;
    quality: "excellent" | "good" | "fair" | "poor" | "unknown";
    hasNetworkIssues: boolean;
    lastWarning?: string;
    suggestions?: string[];
  } {
    if (!this.activeCall) {
      return {
        isActive: false,
        quality: "unknown",
        hasNetworkIssues: false,
      };
    }

    // This would need to be enhanced to store the last quality sample
    // For now, return basic information
    return {
      isActive: true,
      quality: "unknown", // Would be updated by analyzeCallQualitySample
      hasNetworkIssues: false, // Would be updated by analyzeCallQualitySample
    };
  }

  /**
   * Check if the current call has network quality issues that may affect transcription
   */
  public hasNetworkQualityIssues(): boolean {
    // This could be enhanced to track recent warnings
    return false;
  }

  async muteCall({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (message: string) => void;
    onFailure: (message: string, error: string) => void;
  }): Promise<boolean> {
    if (!this.activeCall) {
      onFailure("Mute failed", "No active call");
      return false;
    }

    try {
      this.activeCall.mute(true);
      onSuccess("Call muted");
      return true;
    } catch (error) {
      onFailure("Mute failed", (error as Error).message);
      return false;
    }
  }

  async unmuteCall({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (message: string) => void;
    onFailure: (message: string, error: string) => void;
  }): Promise<boolean> {
    if (!this.activeCall) {
      onFailure("Unmute failed", "No active call");
      return false;
    }

    try {
      this.activeCall.mute(false);
      onSuccess("Call unmuted");
      return true;
    } catch (error) {
      onFailure("Unmute failed", (error as Error).message);
      return false;
    }
  }

  async disconnect(): Promise<boolean> {
    const unregisterTimeout = new Promise(
      (_, reject) =>
        setTimeout(() => reject(new Error("Unregister timed out")), 5000) // 5-second timeout
    );

    try {
      if (this.device) {
        this.log("info", "Attempting to unregister device...");
        // Using Promise.race to resolve either unregister or timeout
        await Promise.race([this.device.unregister(), unregisterTimeout]);
        this.log("info", "Device unregistered successfully");
      } else {
        this.log("info", "Device is null or undefined, skipping unregister.");
      }
      this.data.setDeviceReady(false);
      this.data.setCallSid("");
      this.callSid = null;
      this.activeCall = null;

      return true;
    } catch (error) {
      this.log("error", "Failed to disconnect device:", error);
      return false;
    }
  }

  async makeCall(
    from: string, // Twilio phone number
    to: string,
    ext: string, // The number or client identifier to call
    {
      onStatus,
      onFailure,
    }: {
      onStatus: (message: string) => void;
      onFailure: (message: string, error: string) => void;
    }
  ): Promise<boolean> {
    if (!this.device || !this.data.deviceReady) {
      onFailure("Connection failed", "Device not ready");
      return false;
    }

    try {
      // Make an outgoing call with both 'from' and 'to'
      this.activeCall = await this.device.connect({
        params: {
          from: from, // Your Twilio number
          to: to, // Phone number or client identifier
          extension: ext,
        },
      });
      // Event listeners to track the call status
      this.setupCallEventListeners(onStatus, onFailure);
      return true;
    } catch (error) {
      console.error("Connection failed", error);
      onStatus("failed");
      this.updateCallState(CallStatus.Failed, null, true);
      onFailure("Connection failed", (error as Error).message);
      return false;
    }
  }

  private setupCallEventListeners(
    onStatus: (message: string) => void,
    onFailure: (message: string, error: string) => void
  ) {
    if (!this.activeCall) return;

    this.activeCall.on("accept", () => {
      const callSid = this.activeCall.parameters.CallSid;
      this.updateCallState(CallStatus.InProgress, callSid, false);
      onStatus("accepted");

      // ✅ Auto-enable transcription with delay to ensure call is stable
      // setTimeout(() => {
      //   this.autoEnableTranscription();
      // }, 1000); // 1 second delay
    });

    this.activeCall.on("answered", () => {
      const callSid = this.activeCall.parameters.CallSid;
      this.updateCallState(CallStatus.InProgress, callSid, false);
      onStatus("accepted");

      // ✅ Auto-enable transcription with delay to ensure call is stable
      // setTimeout(() => {
      //   this.autoEnableTranscription();
      // }, 1000); // 1 second delay
    });

    this.activeCall.on("ringing", () => {
      const callSid = this.activeCall.parameters.CallSid;
      this.updateCallState(CallStatus.Ringing, callSid, false);
      this.log("info", "Ringing");
      console.log("Ringing -> CallId: ", callSid);
      onStatus("ringing");
    });

    this.activeCall.on("open", () => {
      this.log("info", "Call InProgress");
      this.updateCallState(CallStatus.InProgress, this.callSid, false);
      onStatus("open");

      // ✅ Auto-enable transcription with delay to ensure call is stable
      // setTimeout(() => {
      //   this.autoEnableTranscription();
      // }, 1000); // 1 second delay
    });

    this.activeCall.on("initiated", () => {
      this.log("info", "Call initiated");
      const callSid = this.activeCall.parameters.CallSid;
      this.updateCallState(CallStatus.Initiated, callSid, false);
      onStatus("initiated");
    });

    this.activeCall.on("no-answer", () => {
      this.log("info", "No answer");
      this.updateCallState(CallStatus.Unanswered, null, true);
      onStatus("no-answer");
    });

    this.activeCall.on("connecting", () => {
      this.log("info", "Call connecting");
      this.updateCallState(CallStatus.Connecting, this.callSid, false);
      onStatus("connecting");
    });

    this.activeCall.on("reject", () => {
      this.log("info", "Call rejected (busy)");
      this.updateCallState(CallStatus.Busy, null, true);
      onStatus("busy");
    });

    this.activeCall.on("completed", () => {
      this.log("info", "Call completed");
      //this.disableTranscription();
      this.updateCallState(CallStatus.Completed, null, true);
      onStatus("completed");
    });

    this.activeCall.on("error", (error) => {
      this.log("info", "Call error");
      //this.disableTranscription();
      this.updateCallState(CallStatus.Failed, null, true);
      onStatus("failed");
      onFailure("Call error", error.message);
    });

    this.activeCall.on("warning", (warningName, warningData) => {
      //console.warn(`Call warning: ${warningName}`, warningData);
      this.log("warn", `Call warning: ${warningName}`, warningData);
    });

    this.activeCall.on("disconnect", (connection) => {
      const status = connection.status();
      // 🛑 STOP TRANSCRIPTION IMMEDIATELY when call ends
      //this.disableTranscription();

      // Handle different disconnect scenarios
      if (status === "completed" || status === "failed" || status === "busy") {
        this.updateCallState(CallStatus.Completed, null, true);
        onStatus("disconnected");
      } else if (status === "no-answer" || status === "rejected") {
        this.updateCallState(CallStatus.Unanswered, null, true);
        onStatus("disconnected");
      } else {
        // Temporary disconnection - don't cleanup yet
        this.updateCallState(CallStatus.Closed, null, true);
        onStatus("completed");
      }
    });

    //this.monitorCallQuality();
  }

  async disconnectCall({
    onFailure,
  }: {
    onFailure: (message: string) => void;
  }): Promise<boolean> {
    if (!this.activeCall) {
      onFailure("No active call");
      return false;
    }

    try {
      await this.activeCall.disconnect();
      // Don't immediately cleanup - let the disconnect event handle it
      this.log("info", "Call disconnect requested");
      return true;
    } catch (error) {
      this.log("error", "Failed to disconnect call:", error);
      onFailure("Failed to disconnect call " + (error as Error).message);
      return false;
    }
  }

  public static getPurifiedPhoneNumber(
    phoneNumber: string,
    d3eParams?: Partial<{ defaultCountryCode: string }>
  ): string {
    let defaultCountryCode = d3eParams?.defaultCountryCode ?? "+1";

    // Remove all special characters except +
    let purifiedNumber: string = phoneNumber.replaceAll(/[^\d+]/g, "");

    // If the number doesn't start with +, prepend the default country code
    if (!purifiedNumber.startsWith("+")) {
      purifiedNumber = defaultCountryCode + purifiedNumber;
    }
    return purifiedNumber;
  }

  public addNotificationListner({
    onNotificationClick,
  }: {
    onNotificationClick: (actionType: boolean, action: string) => void;
  }): void {
    // Add a listener for the notification click event
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data.type === "NOTIFICATION_ACTION") {
        // Handle any action type
        console.log("Action received:", event.data.action);
        console.log("Notification data:", event.data.notificationData);
        onNotificationClick(true, event.data.action);
      } else {
        onNotificationClick(false, "");
      }
    });
  }
}
